const getMilestonePlanningSummary = (milestone, initiative) => {
  const history = initiative?.history || [];
  const mId = milestone.milestoneNumber;
  
  const startOfDay = (date) => {
    if (!date) return null;
    const dateObj = date instanceof Date ? date : new Date(date);
    return new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());
  };
  
  // 1. Setup Dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = milestone.startDate ? new Date(milestone.startDate) : null;
  const endDate = milestone.endDate ? new Date(milestone.endDate) : null;
  const msInDay = 24 * 60 * 60 * 1000;
  
  // FIXED: Calculate days differences with proper date normalization
  const normalizedToday = startOfDay(today);
  const normalizedStartDate = startDate ? startOfDay(startDate) : null;
  const normalizedEndDate = endDate ? startOfDay(endDate) : null;
  
  // FIXED: Use Math.ceil for future dates (including today)
  const daysUntilStart = normalizedStartDate && normalizedStartDate >= normalizedToday
    ? Math.ceil((normalizedStartDate.getTime() - normalizedToday.getTime()) / msInDay)
    : null;
  const daysUntilEnd = normalizedEndDate && normalizedEndDate >= normalizedToday
    ? Math.ceil((normalizedEndDate.getTime() - normalizedToday.getTime()) / msInDay)
    : null;
  
  // 2. Extract Actual Dates from History
  const actualStartEntry = history
    .filter(
      h =>
        h.number === mId &&
        h.changes?.status?.previous === 'Not Started' &&
        h.changes?.status?.new === 'Started'
    )
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
  
  const actualCompletedEntry = history
    .filter(
      h =>
        h.number === mId &&
        h.changes?.status?.previous === 'Started' &&
        h.changes?.status?.new === 'Completed'
    )
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];
  
  const actualStartDate = actualStartEntry
    ? new Date(actualStartEntry.timestamp)
    : null;
  
  const actualCompletedDate = actualCompletedEntry
    ? new Date(actualCompletedEntry.timestamp)
    : null;
  
  // --- LOGIC ENGINE ---
  
  // 1. Immediate Exits
  if (milestone.status === 'Cancelled') return 'Cancelled';
  if (milestone.status === 'Not planned') return 'Not planned';
  if (milestone.status === 'Not Started' && (!startDate || !endDate)) return '<not set>';
  
  // 2. Completed Status Analysis
  if (milestone.status === 'Completed') {
    if (actualCompletedDate && endDate && startOfDay(actualCompletedDate) > startOfDay(endDate)) {
      const daysLate = Math.floor(
        (startOfDay(actualCompletedDate) - startOfDay(endDate)) / msInDay
      );
      if (daysLate < 7) return `Lately Completed (less than 1 week)`;
      if (daysLate <= 28) return `Lately Completed (1 to 4 weeks)`;
      return `Lately Completed (more than 4 weeks)`;
    }
    return 'Completed';
  }
  
  // 3. STARTED MILESTONES
  if (milestone.status === 'Started') {
    // Priority 1: Late to Finish (end date passed)
    if (endDate && today > endDate) {
      const daysLate = Math.floor((today - endDate) / msInDay);
      if (daysLate < 7) return `Late (less than 1 week)`;
      if (daysLate <= 28) return `Late (1 to 4 weeks)`;
      return `Late (more than 4 weeks)`;
    }
    
    // Priority 2: Due Soon (remaining to complete) - FIXED: <= 30 includes 30 days
    if (daysUntilEnd !== null && daysUntilEnd <= 30) {
      if (daysUntilEnd <= 7) {
        return 'Due soon (7 days)';
      } else if (daysUntilEnd <= 30) {
        return 'Due soon (30 days)';
      }
    }
    
    // Priority 3: Lately Started
    if (actualStartDate && startDate && startOfDay(actualStartDate) > startOfDay(startDate)) {
      const daysLate = Math.floor(
        (startOfDay(actualStartDate) - startOfDay(startDate)) / msInDay
      );
      if (daysLate < 7) return `Lately Started (less than 1 week)`;
      if (daysLate <= 28) return `Lately Started (1 to 4 weeks)`;
      return `Lately Started (more than 4 weeks)`;
    }
    
    return 'On plan';
  }
  
  // 4. NOT STARTED MILESTONES
  if (milestone.status === 'Not Started') {
    // Priority 1: Late to Finish (end date passed)
    if (endDate && today > endDate) {
      const daysLate = Math.floor((today - endDate) / msInDay);
      if (daysLate < 7) return `Late (less than 1 week)`;
      if (daysLate <= 28) return `Late (1 to 4 weeks)`;
      return `Late (more than 4 weeks)`;
    }
    
    // Priority 2: Due Soon (remaining to start) - FIXED: <= 30 includes 30 days
    if (daysUntilStart !== null && daysUntilStart <= 30) {
      if (daysUntilStart <= 7) {
        return 'Due soon (7 days)';
      } else if (daysUntilStart <= 30) {
        return 'Due soon (30 days)';
      }
    }
    
    // Priority 3: Late to Start
    if (startDate && today > startDate && endDate && today < endDate) {
      const daysLate = Math.floor((today - startDate) / msInDay);
      if (daysLate < 7) return `Late to start (less than 1 week)`;
      if (daysLate <= 28) return `Late to start (1 to 4 weeks)`;
      return `Late to start (more than 4 weeks)`;
    }
    
    // Priority 4: On Plan (start date > 30 days away)
    // FIXED: Use > 30 (not >=) so exactly 30 days goes to Due soon
    if (daysUntilStart !== null && daysUntilStart > 30) {
      return 'On plan';
    }
    
    return 'Not started';
  }
  
  return 'On plan';
};

export default getMilestonePlanningSummary;